import React, { useState, useEffect } from "react"
import { Button, Flex, Card, Spin } from "antd"
import { FileTextOutlined, DownloadOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { getCrewDocuments, updateCrewProfile } from "store/crew-profiles"
import { toaster } from "components/common/Toast"
import { spacings, colors } from "themes/variables"
import LicensesModal from "./modal/LicensesModal"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"
import PropTypes from "prop-types"
import LicenseCertificate from "ui/Icon/DSIcon/LicenseCertificate"
import { downloadFile } from "utils/file-helpers"

const LicensesCertifications = ({ crewMemberId, can_edit }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Get documents from Redux store
  const { data: documentsData = [], loading } = useSelector((state) => state.crewProfiles.documents)
  const { loading: updating } = useSelector((state) => state.crewProfiles.update)

  // Fetch documents on mount
  useEffect(() => {
    if (crewMemberId) {
      dispatch(getCrewDocuments(crewMemberId))
    }
  }, [dispatch, crewMemberId])
  // Transform API data to component format
  const licenses = documentsData.map((doc) => ({
    id: doc.id,
    uid: `doc-${doc.id}`,
    name: doc.document_name,
    fileName: doc.file_name,
    fileUrl: doc.file_url,
    type: doc.document_type,
    status: doc.status,
    issueDate: doc.issue_date,
    expiryDate: doc.expiry_date,
    authority: doc.issuing_authority,
    notes: doc.notes
  }))

  const handleEdit = () => {
    setIsModalOpen(true)
  }

  const handleSave = async (staffDocumentsAttributes) => {
    try {
      // Create FormData for the batch update
      const formData = new FormData()

      // Append each document in the staff_documents_attributes array
      staffDocumentsAttributes.forEach((doc, index) => {
        if (doc._destroy && doc.id) {
          // For deletions: send id and _destroy flag
          formData.append(`profile[staff_documents_attributes][${index}][id]`, doc.id)
          formData.append(`profile[staff_documents_attributes][${index}][_destroy]`, "true")
        } else if (doc.file) {
          // For new uploads: send file
          formData.append(`profile[staff_documents_attributes][${index}][file]`, doc.file)
        }
      })

      // Make single API call to update profile with documents
      await dispatch(updateCrewProfile(crewMemberId, formData))

      // Refresh the documents list
      await dispatch(getCrewDocuments(crewMemberId))

      toaster.success(t("crewMember.licenses.messages.uploadSuccess", { defaultValue: "Documents updated successfully" }))
      setIsModalOpen(false)
    } catch (error) {
      console.error("Error saving documents:", error)
      toaster.error(t("crewMember.licenses.messages.uploadError", { defaultValue: "Failed to update documents" }))
    }
  }

  const handleDownload = (license) => {
    downloadFile(license.fileUrl, license.name || "document")
  }

  // Show loading state
  if (loading && !documentsData.length) {
    return (
      <Flex vertical gap={spacings.M} className={styles.container}>
        <Flex align="center" justify="space-between" className={styles.header}>
          <h2 className={styles.title}>{t("crewMember.licenses.title")}</h2>
        </Flex>
        <Card className="py-5xl">
          <Flex justify="center" align="center">
            <Spin size="large" />
          </Flex>
        </Card>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={spacings.M} className={styles.container}>
      {/* Header */}
      <Flex align="center" justify="space-between" className={styles.header}>
        <h2 className={styles.title}>{t("crewMember.licenses.title")}</h2>
        {can_edit && (
          <Button type="link" onClick={handleEdit} className={styles.editButton}>
            {t("global.edit")}
          </Button>
        )}
      </Flex>

      {/* List with Empty State */}
      <WithEmptyStateWrapper
        name="licenses"
        hasItems={licenses.length > 0}
        fallback={
          <Card className="py-5xl">
            <Flex vertical justify="center" align="center" gap={12}>
              <LicenseCertificate />
              <Text color={colors.textPrimary} weight="semibold">
                {t("crewMember.licenses.emptyState.title")}
              </Text>
              <Text color={colors.textSecondaryDark}>{t("crewMember.licenses.emptyState.subtitle")}</Text>
            </Flex>
          </Card>
        }
      >
        <Flex vertical gap={spacings.XS} className={styles.list}>
          {licenses.map((license) => (
            <Flex key={license?.uid} align="center" justify="space-between" gap={spacings.L} className={styles.item}>
              <Flex align="center" gap={spacings.XXS} className={styles.fileInfo}>
                <FileTextOutlined className={styles.fileIcon} />
                <span className={styles.fileName} title={license?.name}>
                  {license?.name}
                </span>
              </Flex>
              <Button type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(license)} className={styles.downloadButton} />
            </Flex>
          ))}
        </Flex>
      </WithEmptyStateWrapper>

      {/* Licenses Modal */}
      <LicensesModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onSave={handleSave}
        mode="edit"
        initialFiles={licenses.map((license) => ({
          uid: license.uid,
          id: license.id,
          name: license.name,
          status: "done",
          url: license.fileUrl
        }))}
        loading={updating}
      />
    </Flex>
  )
}

LicensesCertifications.propTypes = {
  crewMemberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  can_edit: PropTypes.bool
}

export default LicensesCertifications
